import '@theme/dialog';

const STORAGE_PREFIX = 'bettina-newsletter-popup';
const DAY_MS = 24 * 60 * 60 * 1000;

const FREQUENCY_MS = {
  day: DAY_MS,
  week: 7 * DAY_MS,
};

class BettinaNewsletterPopup extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.dialogComponent = this.querySelector('dialog-component');
    this.dialog = this.querySelector('dialog');
    this.closeButton = this.querySelector('[data-newsletter-popup-close]');
    this.focusTarget = this.querySelector('[data-newsletter-popup-focus]');
    this.recordDismissalOnClose = true;
    this.hasOpened = false;

    this.closeButton?.addEventListener('click', this.close, { signal: this.abortController.signal });
    this.dialogComponent?.addEventListener('dialog:close', this.handleDialogClose, {
      signal: this.abortController.signal,
    });

    this.setupThemeEditorPreview();

    if (window.Shopify?.designMode) return;

// Don't show the newsletter popup to visitors arriving from a Mailchimp email.
if (this.isMailchimpVisitor()) return;

const statusMessage = this.querySelector('[data-newsletter-popup-status]');
    if (statusMessage instanceof HTMLElement) {
      const isSuccess = statusMessage.dataset.newsletterPopupStatus === 'success';
      this.open({ focusElement: statusMessage, recordDismissalOnClose: isSuccess });
      return;
    }

    if (this.shouldSuppress()) return;

    this.setupTrigger();
  }

  disconnectedCallback() {
    window.clearTimeout(this.triggerTimeout);
    this.abortController?.abort();
  }

  setupThemeEditorPreview() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    document.addEventListener(
      'shopify:section:select',
      (event) => {
        if (event.detail?.sectionId === sectionId) {
          this.open({ recordDismissalOnClose: false });
        }
      },
      { signal: this.abortController.signal },
    );

    document.addEventListener(
      'shopify:section:deselect',
      (event) => {
        if (event.detail?.sectionId === sectionId) {
          this.close();
        }
      },
      { signal: this.abortController.signal },
    );
  }

  setupTrigger() {
    switch (this.dataset.trigger) {
      case 'scroll':
        this.setupScrollTrigger();
        break;
      case 'exit_intent':
        this.setupExitIntentTrigger();
        break;
      case 'delay':
      default:
        this.setupDelayTrigger();
        break;
    }
  }

  setupDelayTrigger() {
    const seconds = Number.parseInt(this.dataset.delay || '2', 10);
    const delay = Number.isFinite(seconds) ? Math.max(seconds, 1) * 1000 : 2000;

    this.triggerTimeout = window.setTimeout(() => this.open(), delay);
  }

  setupScrollTrigger() {
    const percent = Number.parseInt(this.dataset.scrollPercent || '45', 10);
    const threshold = Math.min(Math.max(percent, 10), 90) / 100;

    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;

      if (window.scrollY / scrollable >= threshold) {
        window.removeEventListener('scroll', onScroll);
        this.open();
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true, signal: this.abortController.signal });
  }

  setupExitIntentTrigger() {
    const onMouseLeave = (event) => {
      if (event.clientY > 0) return;
      document.documentElement.removeEventListener('mouseleave', onMouseLeave);
      this.open();
    };

    document.documentElement.addEventListener('mouseleave', onMouseLeave, { signal: this.abortController.signal });
  }

  open = ({ focusElement = this.focusTarget, recordDismissalOnClose = true } = {}) => {
    if (this.hasOpened) return;
    if (!this.dialogComponent || !this.dialog) return;
    if (this.hasActiveModal()) return;

    this.hasOpened = true;
    this.recordDismissalOnClose = recordDismissalOnClose;

    if (typeof this.dialogComponent.showDialog === 'function') {
      this.dialogComponent.showDialog();
    } else if (!this.dialog.open) {
      this.dialog.showModal();
    }

    window.setTimeout(() => {
      if (focusElement instanceof HTMLElement && focusElement.isConnected) {
        focusElement.focus();
      }
    }, 80);
  };

  close = () => {
    if (!this.dialogComponent) return;

    if (typeof this.dialogComponent.closeDialog === 'function') {
      this.dialogComponent.closeDialog();
    }
  };

  handleDialogClose = () => {
    this.hasOpened = false;

    if (this.recordDismissalOnClose) {
      this.recordDismissal();
    }
  };
isMailchimpVisitor() {
  const params = new URLSearchParams(window.location.search);

  const isMailchimp =
    params.has('mc_cid') ||
    params.has('mc_eid') ||
    params.get('utm_source')?.toLowerCase() === 'mailchimp';

  if (!isMailchimp) {
    return this.safeGet(window.sessionStorage, `${STORAGE_PREFIX}:mailchimp`) === '1';
  }

  this.safeSet(window.sessionStorage, `${STORAGE_PREFIX}:mailchimp`, '1');
  return true;
}
  hasActiveModal() {
    return Array.from(document.querySelectorAll('dialog[open]')).some((dialog) => dialog !== this.dialog);
  }

  get storageKey() {
    return `${STORAGE_PREFIX}:${this.dataset.sectionId || 'global'}`;
  }

  get frequency() {
    return this.dataset.frequency || 'session';
  }

  shouldSuppress() {
    const frequency = this.frequency;

    if (frequency === 'always') return false;

    if (frequency === 'session') {
      return this.safeGet(window.sessionStorage, this.storageKey) === '1';
    }

    const raw = this.safeGet(window.localStorage, this.storageKey);
    if (!raw) return false;

    if (frequency === 'once') return true;

    const timestamp = Number.parseInt(raw, 10);
    const maxAge = FREQUENCY_MS[frequency];

    if (!Number.isFinite(timestamp) || !maxAge) {
      this.safeRemove(window.localStorage, this.storageKey);
      return false;
    }

    return Date.now() - timestamp < maxAge;
  }

  recordDismissal() {
    const frequency = this.frequency;

    if (frequency === 'always') return;

    if (frequency === 'session') {
      this.safeSet(window.sessionStorage, this.storageKey, '1');
      return;
    }

    this.safeSet(window.localStorage, this.storageKey, String(Date.now()));
  }

  safeGet(storage, key) {
    try {
      return storage?.getItem(key);
    } catch {
      return null;
    }
  }

  safeSet(storage, key, value) {
    try {
      storage?.setItem(key, value);
    } catch {
      // Storage access can be blocked by browser settings.
    }
  }

  safeRemove(storage, key) {
    try {
      storage?.removeItem(key);
    } catch {
      // Storage access can be blocked by browser settings.
    }
  }
}

if (!customElements.get('bettina-newsletter-popup')) {
  customElements.define('bettina-newsletter-popup', BettinaNewsletterPopup);
}
